/**
 * Système de logging conditionnel pour Ekose-Rx
 * Évite l'exposition de logs sensibles en production
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabledInProduction: boolean;
  minLevel: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
}

class Logger {
  private config: LoggerConfig;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.config = {
      enabledInProduction: false,
      minLevel: this.isDevelopment ? 'debug' : 'error',
      enableConsole: this.isDevelopment,
      enableRemote: !this.isDevelopment,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.isDevelopment && !this.config.enabledInProduction) {
      return level === 'error'; // Seules les erreurs en production
    }
    
    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };
    
    return levels[level] >= levels[this.config.minLevel];
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    const prefix = this.isDevelopment ? 
      `[${timestamp}] ${level.toUpperCase()}:` : 
      `${level.toUpperCase()}:`;
    
    return `${prefix} ${message}`;
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug') && this.config.enableConsole) {
      console.debug(this.formatMessage('debug', message), ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info') && this.config.enableConsole) {
      console.info(this.formatMessage('info', message), ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      if (this.config.enableConsole) {
        console.warn(this.formatMessage('warn', message), ...args);
      }
      // TODO: Envoyer vers service de monitoring en production
    }
  }

  error(message: string, error?: Error | unknown, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      const errorMessage = this.formatMessage('error', message);
      
      if (this.config.enableConsole) {
        console.error(errorMessage, error, ...args);
      }
      
      // En production, envoyer vers service de monitoring
      if (this.config.enableRemote && !this.isDevelopment) {
        this.sendToRemoteLogging();
      }
    }
  }

  private async sendToRemoteLogging(): Promise<void> {
    try {
      // TODO: Intégrer avec Sentry ou autre service de monitoring
      // await monitoringService.logError({ message, error, args });
    } catch (remoteError) {
      // Fallback silencieux en cas d'échec du logging distant
      if (this.isDevelopment) {
        logger.warn('Failed to send log to remote service:', remoteError);
      }
    }
  }

  // Méthodes spécialisées pour les contextes Ekose
  auth(message: string, context?: Record<string, unknown>): void {
    this.debug(`🔐 [AUTH] ${message}`, context || {});
  }

  api(message: string, context?: Record<string, unknown>): void {
    this.debug(`🌐 [API] ${message}`, context || {});
  }

  socket(message: string, context?: Record<string, unknown>): void {
    this.debug(`🔌 [SOCKET] ${message}`, context || {});
  }

  webrtc(message: string, context?: Record<string, unknown>): void {
    this.debug(`📹 [WEBRTC] ${message}`, context || {});
  }

  dashboard(message: string, context?: Record<string, unknown>): void {
    this.debug(`📊 [DASHBOARD] ${message}`, context || {});
  }

  // Specialized dashboard logging methods
  adminDashboard(message: string, context?: Record<string, unknown>): void {
    this.debug(`🏥 [ADMIN-DASHBOARD] ${message}`, context || {});
  }

  doctorDashboard(message: string, context?: Record<string, unknown>): void {
    this.debug(`👨‍⚕️ [DOCTOR-DASHBOARD] ${message}`, context || {});
  }

  patientDashboard(message: string, context?: Record<string, unknown>): void {
    this.debug(`👤 [PATIENT-DASHBOARD] ${message}`, context || {});
  }

  // Performance logging
  performance(metric: string, value: number, context?: Record<string, unknown>): void {
    this.info(`⚡ [PERFORMANCE] ${metric}: ${value}ms`, context || {});
  }

  // Error logging with context
  errorWithContext(message: string, error: Error | unknown, context?: Record<string, unknown>): void {
    const errorObj = error instanceof Error ? {
      message: error.message,
      stack: error.stack,
      name: error.name,
    } : error;

    this.error(message, { error: errorObj, ...context });
  }

  // User action logging
  userAction(action: string, userId?: number, userRole?: string, context?: Record<string, unknown>): void {
    this.info(`👆 [USER-ACTION] ${action}`, {
      userId,
      userRole,
      ...context,
    });
  }

  // Configuration dynamique
  setConfig(newConfig: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

// Instance singleton
export const logger = new Logger();

// Exports pour compatibilité
export default logger;

// Types pour TypeScript
export type { LogLevel, LoggerConfig };
